import { Buffer } from "buffer";
import { AnchorProvider, Program } from "@coral-xyz/anchor";
import { PublicKey, VersionedTransactionResponse } from "@solana/web3.js";
import { getChampionship } from "./championship";
import { fetchSubmissionsWithVotes } from "./submission";

// 🎯 Prize Distribution Constants
export const PLATFORM_COMMISSION_PERCENT = 0.15; // 15%
export const BURN_PERCENT = 0.05; // 5% of total pool
export const FUTURE_REWARDS_PERCENT = 0.10; // 10% of total pool
export const FIRST_PLACE_PERCENT = 0.60; // 60% of remaining pool
export const SECOND_PLACE_PERCENT = 0.25; // 25%
export const THIRD_PLACE_PERCENT = 0.10; // 10%
export const CREATOR_SHARE = 0.70; // 70% of reward goes to fart creators
export const VOTER_SHARE = 0.30; // 30% of reward goes to voters

/**
 * Fetches the total prize pool by summing up token payments in transactions.
 */
export async function getCurrentPrizePool(program: Program<any>, provider: AnchorProvider, championshipUuid: string, serverSubmissions?: any[]) {
  console.log(`🔍 Fetching Prize Pool for Championship: ${championshipUuid}`);
  const submissionsWithVotes = serverSubmissions ? serverSubmissions : await fetchSubmissionsWithVotes(program, provider, championshipUuid);
  let totalPrizePool = 0;
  const userStats: Record<string, { paid: number; submissions: number; votes: number }> = {};

  for (const baseSubmission of submissionsWithVotes) {
    const submission = baseSubmission.programSubmission || baseSubmission;
    const submitterPublicKey = new PublicKey(submission.user).toBase58();
    // ✅ Initialize submitter data
    if (!userStats[submitterPublicKey]) {
      userStats[submitterPublicKey] = { paid: 0, submissions: 0, votes: 0 };
    }
    userStats[submitterPublicKey].submissions += 1; // ✅ Count submissions
    const submissionPayment = Number(submission.amountPaidRaw ?? 0);
    userStats[submitterPublicKey].paid += submissionPayment;
    totalPrizePool += submissionPayment;

    for (const vote of submission.votes) {
      const voterPublicKey = new PublicKey(vote.voter).toBase58();
      // ✅ Initialize voter data
      if (!userStats[voterPublicKey]) {
        userStats[voterPublicKey] = { paid: 0, submissions: 0, votes: 0 };
      }
      userStats[voterPublicKey].votes += 1; // ✅ Count votes
      const votePayment = Number(vote.amountPaidRaw ?? 0);
      userStats[voterPublicKey].paid += votePayment;
      totalPrizePool += votePayment;
    }
  }

  console.log(`💰 Total Prize Pool: ${totalPrizePool.toFixed(2)} Tokens`);
  return { submissionsWithVotes, totalPrizePool, userStats };
}

export const getPrizeBreakdown = (total) => {
  const commission = total * PLATFORM_COMMISSION_PERCENT;
  const burn = total * BURN_PERCENT;
  const future = total * FUTURE_REWARDS_PERCENT;
  const afterDeductions = total - commission;

  const first = afterDeductions * FIRST_PLACE_PERCENT;
  const second = afterDeductions * SECOND_PLACE_PERCENT;
  const third = afterDeductions * THIRD_PLACE_PERCENT;

  return {
    totalCollected: total,
    commission,
    burn,
    future,
    first: {
      total: first,
      creator: first * CREATOR_SHARE,
      voters: first * VOTER_SHARE,
    },
    second: {
      total: second,
      creator: second * CREATOR_SHARE,
      voters: second * VOTER_SHARE,
    },
    third: {
      total: third,
      creator: third * CREATOR_SHARE,
      voters: third * VOTER_SHARE,
    },
  };
};



/**
 * Generates a mock VersionedTransactionResponse for testing.
 * @param txId - The fake transaction ID.
 * @param payer - The payer's public key.
 * @param amount - The token amount being transferred.
 * @returns A mock VersionedTransactionResponse object.
 */
export function generateMockTransactionResponse(txId: string, payer: PublicKey, amount: number): VersionedTransactionResponse {
  return {
    transaction: {
      message: { accountKeys: [] },
      signatures: [txId],
    },
    meta: {
      postTokenBalances: [
        {
          accountIndex: 0,
          mint: "MockMintTokenAddress",
          owner: new PublicKey(payer).toBase58(),
          uiTokenAmount: {
            amount: amount.toString(),
            decimals: 9,
            uiAmount: amount,
            uiAmountString: amount.toString(),
          },
        },
      ],
    },
  } as VersionedTransactionResponse;
}

/**
 * Calculates prize distribution based on ranking.
 */
export async function calculatePrizeDistribution(program: Program<any>, provider: AnchorProvider, championshipUuid: string, serverSubmissions?: any[]) {
  console.log(`🔍 Calculating prize distribution for Championship: ${championshipUuid}`);
  const connection = provider.connection;
  // ✅ Fetch championship details from backend
  const championship = await getChampionship(program, championshipUuid);

  // ✅ Check if championship is active
  if (!championship.isActive) {
    throw new Error(`❌ Championship ${championshipUuid} is not active! Prize distribution cannot proceed.`);
  }

  // ✅ Fetch total prize pool and voter payments
  const { submissionsWithVotes, totalPrizePool, userStats } = await getCurrentPrizePool(program, provider, championshipUuid, serverSubmissions);

  // ✅ Check if minimum participant requirement is met
  if (submissionsWithVotes.length < championship.minParticipants) {
    throw new Error(`❌ Championship ${championshipUuid} does not meet the minimum participant requirement of ${championship.minParticipants}.`);
  }

  // ✅ Apply Platform Commission
  const platformCommission = Number(totalPrizePool * PLATFORM_COMMISSION_PERCENT) || 0;
  const burntAmount = Number(totalPrizePool * BURN_PERCENT) || 0;
  const futureRewardsFund = Number(totalPrizePool * FUTURE_REWARDS_PERCENT) || 0;

  // ✅ Remaining pool after commission
  const prizeAfterCommission = Number(totalPrizePool - platformCommission) || 0;

  // ✅ Calculate placement rewards
  const firstPlacePrize = Number(prizeAfterCommission * FIRST_PLACE_PERCENT) || 0;
  const secondPlacePrize = Number(prizeAfterCommission * SECOND_PLACE_PERCENT) || 0;
  const thirdPlacePrize = Number(prizeAfterCommission * THIRD_PLACE_PERCENT) || 0;

  // ✅ Rank submissions by votes
  submissionsWithVotes.sort((a, b) => b.voteCount - a.voteCount);

  const prizeDistribution: Record<string, any> = {};
  const winnerSummaries: Record<string, any> = {}; // ✅ Track winner details
  if (submissionsWithVotes.length > 0) {
    const firstPlace = submissionsWithVotes[0];
    prizeDistribution[firstPlace.uuid] = distributeRewards(firstPlace, firstPlacePrize, userStats, winnerSummaries);
  }
  if (submissionsWithVotes.length > 1) {
    const secondPlace = submissionsWithVotes[1];
    prizeDistribution[secondPlace.uuid] = distributeRewards(secondPlace, secondPlacePrize, userStats, winnerSummaries);
  }
  if (submissionsWithVotes.length > 2) {
    const thirdPlace = submissionsWithVotes[2];
    prizeDistribution[thirdPlace.uuid] = distributeRewards(thirdPlace, thirdPlacePrize, userStats, winnerSummaries);
  }

  // ✅ Collect full payments data across all submissions & votes
  const totalPaidByUsers = Object.values(userStats).reduce((sum, user) => sum + (user.paid || 0), 0);

  return {
    totalPrizePool,
    platformCommission,
    burntAmount,
    futureRewardsFund,
    totalPaidByUsers,
    prizeDistribution,
    winnerSummaries, // ✅ Include winner summaries
  };
}

/**
 * Retrieves total prizes for a given wallet.
 */
export function getWalletPrizes(wallet: string, prizeDistribution: Record<string, any>) {
  let totalPrizes = 0;
  const prizeBreakdown: any[] = [];

  Object.keys(prizeDistribution).forEach((submissionUuid) => {
    const submissionData = prizeDistribution[submissionUuid];

    // ✅ Check if the wallet is a submission creator
    if (submissionData.creator.publicKey === wallet) {
      totalPrizes += Number(submissionData.creator.amount);
      prizeBreakdown.push({
        type: "submission",
        submissionId: submissionUuid,
        position: prizeDistribution[submissionUuid].position,
        amount: Number(submissionData.creator.amount),
      });
    }

    // ✅ Check if the wallet is a voter
    submissionData.voters.forEach((voter: any) => {
      if (voter.publicKey === wallet) {
        totalPrizes += Number(voter.amount);
        prizeBreakdown.push({
          type: "vote",
          submissionId: submissionUuid,
          position: prizeDistribution[submissionUuid].position,
          amount: Number(voter.amount),
        });
      }
    });
  });

  return { totalPrizes, prizeBreakdown };
}

/**
 * Distributes rewards between the fart creator and voters and tracks winner summaries.
 */
function distributeRewards(submission: any, prizeAmount: number,
  userStats: Record<string, { paid: number; submissions: number; votes: number }>,
   winnerSummaries: Record<string, any>) {
     const creatorReward = Number(prizeAmount * CREATOR_SHARE) || 0;
     const voterReward = Number(prizeAmount * VOTER_SHARE) || 0;
     const voterCount = submission.votes.length;
     const voterRewards = voterCount > 0 ? voterReward / voterCount : 0;
     // 🎭 **Handle Creator Rewards**
     const creatorWallet = new PublicKey(submission.user).toBase58();

     // console.log(`💰 Creator ${creatorWallet} will receive ${creatorReward.toFixed(2)} Tokens`);

     if (!winnerSummaries[creatorWallet]) {
       winnerSummaries[creatorWallet] = { submissions: 0, votes: 0, totalReward: 0, totalPaid: 0 };
     }

     winnerSummaries[creatorWallet].submissions += 1;
     winnerSummaries[creatorWallet].totalReward += creatorReward;

    // ✅ Ensure the creator's totalPaid, submissions, and votes are correctly set
    const creatorStats = userStats[creatorWallet] || { paid: 0, submissions: 0, votes: 0 };
     winnerSummaries[creatorWallet].totalPaid = creatorStats.paid;
     winnerSummaries[creatorWallet].submissions = creatorStats.submissions;
     winnerSummaries[creatorWallet].votes = creatorStats.votes;

     return {
       creator: {
         publicKey: creatorWallet,
         amount: creatorReward,
       },
       voters: submission.votes.map((vote: any) => {
         const voterWallet = new PublicKey(vote.voter).toBase58();
         // console.log(`💰 Voter ${voterWallet} will receive ${voterRewards.toFixed(2)} Tokens`);

         if (!winnerSummaries[voterWallet]) {
           winnerSummaries[voterWallet] = { submissions: 0, votes: 0, totalReward: 0, totalPaid: 0 };
         }
         winnerSummaries[voterWallet].votes += 1;
         winnerSummaries[voterWallet].totalReward += voterRewards;
         // ✅ Ensure the voter's totalPaid, submissions, and votes are correctly set
        const voterStats = userStats[voterWallet] || { paid: 0, submissions: 0, votes: 0 };
         winnerSummaries[voterWallet].totalPaid = voterStats.paid;
         winnerSummaries[voterWallet].submissions = voterStats.submissions;
         winnerSummaries[voterWallet].votes = voterStats.votes;

         return {
           publicKey: voterWallet,
           amount: Number(voterRewards),
         };
       }),
     };
   }